import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PagesAPI, ProtectedPagesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Markdown from './Markdown';
import MarkdownToolbar from './MarkdownToolbar';

const categoryNames = {
  foreningen: 'Föreningen',
  ansvarsomraden: 'Ansvarsområden',
  nedingen: 'Sjön Nedingen'
};

const categoryOptions = [
  { value: 'foreningen', label: 'Föreningen' },
  { value: 'ansvarsomraden', label: 'Ansvarsområden' },
  { value: 'nedingen', label: 'Sjön Nedingen' }
];

// Common emojis organized by category
const emojiCategories = [
  {
    name: 'Dokument & Kontor',
    emojis: ['📄', '📃', '📑', '📊', '📈', '📉', '📝', '✏️', '🖊️', '📜', '📋', '📁', '📂', '🗂️', '🗃️', '📇', '📰', '📖', '📚', '✉️', '📧', '📨', '📩', '📤', '📥', '📦']
  },
  {
    name: 'Bygg & Verktyg',
    emojis: ['🏗️', '🏠', '🏡', '🏢', '🏭', '🏪', '🏛️', '🏗️', '🧱', '🪵', '🪨', '🪜', '🔨', '⛏️', '⚒️', '🛠️', '🗡️', '⚔️', '🔧', '🪛', '🏹', '🛡️']
  },
  {
    name: 'Natur & Väder',
    emojis: ['🌲', '🌳', '🌴', '🌵', '🌷', '🌸', '🌹', '🌺', '🌻', '🌼', '🌱', '🌿', '☘️', '🍀', '🍁', '🍂', '🍃', '🪴', '🌾', '💐', '🌊', '💧', '💦', '☔', '⛈️', '🌩️', '🌧️', '☁️', '🌤️', '☀️', '⛄', '❄️', '🌨️', '🌬️', '🌪️']
  },
  {
    name: 'Sport & Fritid',
    emojis: ['⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏉', '🎱', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '⛳', '🏹', '🎣', '🤿', '🥊', '🥋', '⛸️', '🎿', '🛷', '🥌', '⛷️', '🏂', '🏋️', '🤼', '🤽', '🤾', '🏌️', '🏇', '🚴', '🚵', '🧗', '🤺', '🏆', '🏅', '🥇', '🥈', '🥉', '🎖️', '🏵️', '🎗️', '🎫', '🎟️', '🎪', '🤹', '🎭', '🩰', '🎨', '🎬', '🎤', '🎧', '🎼', '🎹', '🥁', '🎷', '🎺', '🎸', '🪕', '🎻', '🎲', '♟️', '🎯', '🎳', '🎮', '🎰', '🧩', '🎱']
  },
  {
    name: 'Djur & Natur',
    emojis: ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐽', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🕷️', '🦂', '🐢', '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍', '🦧', '🐘', '🦛', '🦏', '🐪', '🐫', '🦒', '🦘', '🐃', '🐂', '🐄', '🐎', '🐖', '🐏', '🐑', '🦙', '🐐', '🦌', '🐕', '🐩', '🦮', '🐈', '🐓', '🦃', '🕊️', '🐇', '🐁', '🐀', '🐿️', '🦔', '🦝', '🦨', '🦡', '🦦', '🦥', '🐁']
  },
  {
    name: 'Transport & Vägar',
    emojis: ['🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🚚', '🚛', '🚜', '🦯', '🦽', '🦼', '🛴', '🚲', '🛵', '🏍️', '🛺', '🚨', '🚔', '🚍', '🚘', '🚖', '🚡', '🚠', '🚟', '🚃', '🚋', '🚞', '🚝', '🚄', '🚅', '🚈', '🚂', '🚆', '🚇', '🚊', '🚉', '✈️', '🛫', '🛬', '🛩️', '💺', '🛰️', '🚀', '🛸', '🚁', '🛶', '⛵', '🚤', '🛳️', '⛴️', '🚢', '⚓', '⛽', '🚧', '🚦', '🚥', '🚏']
  },
  {
    name: 'Symboler & Skyltar',
    emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐', '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳', '🈶', '🈚', '🈸', '🈺', '🈷️', '✴️', '🆚', '💮', '🉐', '㊙️', '㊗️', '🈴', '🈵', '🈹', '🈲', '🅰️', '🅱️', '🆎', '🆑', '🅾️', '🆘', '❌', '⭕', '🛑', '⛔', '📛', '🚫', '💯', '💢', '♨️', '🚷', '🚯', '🚳', '🚱', '🔞', '📵', '🚭', '❗', '❕', '❓', '❔', '‼️', '⁉️', '🔅', '🔆', '〽️', '⚠️', '🚸', '🔱', '⚜️', '🔰', '♻️', '✅', '🈯', '💹', '❇️', '✨', '❤️‍🔥', '❤️‍🩹', '❌', '⭕', '💯', '💢', '♨️', '🚷', '🚯', '🚳', '🚱', '🔞', '📵', '🚭', '❗', '❕', '❓', '❔', '‼️', '⁉️', '🔅', '🔆', '〽️', '⚠️', '🚸', '🔱', '⚜️', '🔰', '♻️', '✅', '🈯', '💹', '❇️', '✨', '❤️‍🔥', '❤️‍🩹', '💠', '🌀', '➿', '🌐', '🏧', '🈂️', '🛂', '🛃', '🛄', '🛅', '♿', '🚻', '🚮', '🎦', '📶', '🈁', '🔣', 'ℹ️', '🔤', '🔡', '🔠', '🆖', '🆗', '🆙', '🆒', '🆕', '🆓', '0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟', '🔢', '#️⃣', '*️⃣', '⏏️', '▶️', '⏸️', '⏯️', '⏹', '⏺️', '⏭️', '⏮️', '⏩', '⏪', '⏫', '⏬', '◀️', '🔼', '🔽', '➡️', '⬅️', '⬆️', '⬇️', '↗️', '↘️', '↙️', '↖️', '↕️', '↔️', '↪️', '↩️', '�️', '�️', '�️', '�️', '🔀', '🔁', '🔂', '🔄', '🔃', '🎵', '🎶', '➕', '➖', '➗', '✖️', '💲', '💱', '™️', '©️', '®️', '〰️', '➰', '➿', '🔚', '🔙', '🔛', '🔜', '🔝', '✔️', '☑️', '🔘', '🔴', '🟠', '🟡', '🟢', '🔵', '🟣', '⚫', '⚪', '🟤', '🔺', '🔻', '🔸', '🔹', '🔶', '🔷', '🔳', '🔲', '▪️', '▫️', '◾', '◽', '◼️', '◻️', '🟥', '🟧', '🟨', '🟩', '🟦', '🟪', '🟫', '⬛', '⬜', '🔈', '🔇', '🔉', '🔊', '🔔', '🔕', '📣', '📢', '💬', '💭', '🗯️', '🗨️', '💤', '💫', '💨', '💦', '🕳️', '💣', '💥', '💢', '💯', '💥', '💫', '💦', '💨', '🕳️', '💬', '💭', '🗯️', '🗨️']
  },
  {
    name: 'Mat & Dryck',
    emojis: ['🍏', '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🍍', '🥝', '🥥', '🥑', '🍆', '🥔', '🥕', '🌽', '🌶️', '🫑', '🥒', '🥬', '🥦', '🧄', '🧅', '🍄', '🥜', '🌰', '🍞', '🥐', '🥖', '🥨', '🥯', '🥞', '🧇', '🧀', '🍖', '🍗', '🥩', '🥓', '🍔', '🍟', '🍕', '🌭', '🥪', '🌮', '🌯', '🫔', '🥙', '🧆', '🥚', '🍳', '🥘', '🍲', '🫕', '🥣', '🥗', '🍿', '🧈', '🧂', '🥫', '🍱', '🍘', '🍙', '🍚', '🍛', '🍜', '🍝', '🍠', '🍢', '🍣', '🍤', '🍥', '🍡', '🥟', '🥠', '🥡', '🍦', '🍧', '🍨', '🍩', '🍪', '🎂', '🍰', '🧁', '🥧', '🍫', '🍬', '🍭', '🍮', '🍯', '🍼', '🥛', '☕', '🫖', '🍵', '🧃', '🥤', '🧋', '🍶', '🍺', '🍻', '🥂', '🍷', '🥃', '🍸', '🍹', '🧉', '🍾', '🧊', '🥄', '🍴', '🍽️', '🥣', '🥡', '🥢', '🧂']
  },
  {
    name: 'Påsk & Högtider',
    emojis: ['🎄', '🎃', '🎅', '🤶', '🧑‍🎄', '🦌', '⛄', '☃️', '❄️', '🎁', '🎀', '🎉', '🎊', '🎈', '🎎', '🎏', '🎐', '🎑', '🧧', '✨', '🎇', '🎆', '🎋', '🎍', '🎠', '🎡', '🎢', '🎣', '🎳', '🎰', '🎱', '🎲', '🎮', '🎴', '🎵', '🎶', '🎷', '🎸', '🎹', '🎺', '🎻', '🎼', '🥁', '🪘', '🎤', '🎧', '📻', '🎬', '🏹', '🎯', '🥅', '🎱', '🀄', '🎭', '🎨', '🎪', '🎟️', '🎫', '🎗️', '🎖️', '🏆', '🏅', '🥇', '🥈', '🥉']
  },
  {
    name: 'Teknik & Elektronik',
    emojis: ['💻', '🖥️', '🖨️', '⌨️', '🖱️', '🖲️', '🕹️', '🗜️', '💽', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📽️', '📺', '📻', '📡', '🔋', '🔌', '💡', '🔦', '🕯️', '🪔', '🧯', '🛢️', '💸', '💵', '💴', '💶', '💷', '🪙', '💰', '💳', '💎', '⚖️', '🪜', '🧰', '🪛', '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🗡️', '⚔️', '🔪', '🗡️', '🔫', '🏹', '🛡️', '🔮', '🔮', '🪄', '💎', '🔔', '🔕', '📯', '📣', '📢', '💬', '💭', '🗯️', '🗨️', '🕰️', '🕛', '🕧', '🕐', '🕜', '🕑', '🕝', '🕒', '🕞', '🕓', '🕟', '🕔', '🕠', '🕕', '🕡', '🕖', '🕢', '🕗', '🕣', '🕘', '🕤', '🕙', '🕥', '🕚', '🕦', '🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘', '🌙', '🌚', '🌛', '🌜', '🌡️', '☀️', '🌝', '🌞', '⭐', '🌟', '🌠', '☁️', '⛅', '⛈️', '🌤️', '🌥️', '🌦️', '🌧️', '🌨️', '❄️', '🌬️', '💨', '🌪️', '🌫️', '🌈', '☔', '☂️', '⚡', '❄️', '🔥', '💧', '🌊']
  }
];

// Icon selector for editing existing pages
function IconSelector({ value, onChange, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(0);
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <label style={{
        display: 'block',
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#7A8070',
        marginBottom: '0.5rem'
      }}>
        Ikon (emoji eller text)
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <div ref={pickerRef} style={{ position: 'relative' }}>
          <button
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            style={{
              width: '80px',
              padding: '0.75rem 1rem',
              border: '1px solid #D8D0C0',
              borderRadius: '4px',
              fontSize: '1.4rem',
              textAlign: 'center',
              background: disabled ? '#F5F5F5' : '#fff',
              cursor: disabled ? 'not-allowed' : 'pointer',
              lineHeight: 1,
              opacity: disabled ? 0.6 : 1
            }}
          >
            {value || '📄'}
          </button>
          {isOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 100,
              background: '#fff',
              border: '1px solid #D8D0C0',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              padding: '1rem',
              width: '320px',
              maxHeight: '400px',
              overflow: 'hidden',
              marginTop: '0.5rem'
            }}>
              {/* Category tabs */}
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                marginBottom: '0.75rem',
                paddingBottom: '0.5rem',
                borderBottom: '1px solid #E8E0D0',
                overflowX: 'auto'
              }}>
                {emojiCategories.map((cat, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveCategory(idx)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      border: 'none',
                      borderRadius: '4px',
                      background: activeCategory === idx ? '#2C4A2E' : '#F5F0E8',
                      color: activeCategory === idx ? '#fff' : '#4A5240',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
              {/* Emoji grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: '0.4rem',
                maxHeight: '200px',
                overflowY: 'auto',
                padding: '0.25rem'
              }}>
                {emojiCategories[activeCategory].emojis.map((emoji, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      onChange(emoji);
                      setIsOpen(false);
                    }}
                    style={{
                      padding: '0.5rem',
                      border: value === emoji ? '2px solid #2C4A2E' : '1px solid transparent',
                      borderRadius: '4px',
                      background: value === emoji ? '#E8F5E9' : 'transparent',
                      cursor: 'pointer',
                      fontSize: '1.4rem',
                      lineHeight: 1
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <span style={{ fontSize: '1.5rem' }}>{value || '📄'}</span>
        <span style={{ fontSize: '0.8rem', color: '#7A8070' }}>
          Klicka för att välja emoji
        </span>
      </div>
    </div>
  );
}

// Icon selector for new page form - takes value and onChange props
function NewPageIconSelector({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(0);
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <label style={{
        display: 'block',
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#7A8070',
        marginBottom: '0.5rem'
      }}>
        Ikon (emoji, valfritt)
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <div ref={pickerRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            style={{
              width: '80px',
              padding: '0.75rem 1rem',
              border: '1px solid #D8D0C0',
              borderRadius: '4px',
              fontSize: '1.4rem',
              textAlign: 'center',
              background: '#fff',
              cursor: 'pointer',
              lineHeight: 1
            }}
          >
            {value || '📄'}
          </button>
          {isOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 100,
              background: '#fff',
              border: '1px solid #D8D0C0',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              padding: '1rem',
              width: '320px',
              maxHeight: '400px',
              overflow: 'hidden',
              marginTop: '0.5rem'
            }}>
              {/* Category tabs */}
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                marginBottom: '0.75rem',
                paddingBottom: '0.5rem',
                borderBottom: '1px solid #E8E0D0',
                overflowX: 'auto'
              }}>
                {emojiCategories.map((cat, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveCategory(idx)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      border: 'none',
                      borderRadius: '4px',
                      background: activeCategory === idx ? '#2C4A2E' : '#F5F0E8',
                      color: activeCategory === idx ? '#fff' : '#4A5240',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
              {/* Emoji grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: '0.4rem',
                maxHeight: '200px',
                overflowY: 'auto',
                padding: '0.25rem'
              }}>
                {emojiCategories[activeCategory].emojis.map((emoji, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      onChange(emoji);
                      setIsOpen(false);
                    }}
                    style={{
                      padding: '0.5rem',
                      border: value === emoji ? '2px solid #2C4A2E' : '1px solid transparent',
                      borderRadius: '4px',
                      background: value === emoji ? '#E8F5E9' : 'transparent',
                      cursor: 'pointer',
                      fontSize: '1.4rem',
                      lineHeight: 1
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <span style={{ fontSize: '1.5rem' }}>{value || '📄'}</span>
        <span style={{ fontSize: '0.8rem', color: '#7A8070' }}>
          Klicka för att välja emoji
        </span>
      </div>
    </div>
  );
}

export default function PagesEditor() {
  const { category, slug } = useParams();
  const navigate = useNavigate();
  const textareaRef = useRef(null);
  const newPageTextareaRef = useRef(null);
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const canEdit = hasRole('contentCreator');

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sidebar data
  const [pages, setPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(true);

  // Current page data
  const [selectedPage, setSelectedPage] = useState(null);
  const [loadingPage, setLoadingPage] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [icon, setIcon] = useState('');
  const [description, setDescription] = useState('');
  const [originalTitle, setOriginalTitle] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [originalIcon, setOriginalIcon] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');

  // New page form state
  const [showNewPageForm, setShowNewPageForm] = useState(false);
  const [newPageCategory, setNewPageCategory] = useState('foreningen');
  const [newPageSlug, setNewPageSlug] = useState('');
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageContent, setNewPageContent] = useState('');
  const [newPageIcon, setNewPageIcon] = useState('');
  const [newPageDescription, setNewPageDescription] = useState('');

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  // Derived state - dirty status
  const isDirty = useMemo(() => {
    return title !== originalTitle || 
           content !== originalContent || 
           icon !== originalIcon || 
           description !== originalDescription;
  }, [title, content, icon, description, originalTitle, originalContent, originalIcon, originalDescription]);

  // Toast helper
  const showToast = (type, message) => {
    setToast({ type, message });
  };

  // Load pages list on mount
  useEffect(() => {
    const loadPages = async () => {
      try {
        setLoadingPages(true);
        const data = await PagesAPI.getAll();
        setPages(data.pages || []);
      } catch (err) {
        showToast('error', 'Kunde inte ladda sidlistan');
        console.error('Failed to load pages:', err);
      } finally {
        setLoadingPages(false);
      }
    };

    loadPages();
  }, []);

  // Load specific page when route params change
  useEffect(() => {
    const loadPageData = async () => {
      if (!category || !slug) {
        setSelectedPage(null);
        setTitle('');
        setContent('');
        setIcon('');
        setDescription('');
        setOriginalTitle('');
        setOriginalContent('');
        setOriginalIcon('');
        setOriginalDescription('');
        return;
      }

      try {
        setLoadingPage(true);
        const data = await PagesAPI.getPage(category, slug);
        const page = data.page;

        setSelectedPage(page);
        setTitle(page.title);
        setContent(page.content);
        setIcon(page.icon || '');
        setDescription(page.description || '');
        setOriginalTitle(page.title);
        setOriginalContent(page.content);
        setOriginalIcon(page.icon || '');
        setOriginalDescription(page.description || '');
      } catch (err) {
        showToast('error', 'Kunde inte ladda sidan');
        console.error('Failed to load page:', err);
      } finally {
        setLoadingPage(false);
      }
    };

    loadPageData();
  }, [category, slug]);

  // Beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handlePageSelect = (cat, sl) => {
    if (isDirty) {
      const confirmed = window.confirm('Du har osparade ändringar. Vill du lämna sidan utan att spara?');
      if (!confirmed) return;
    }
    navigate(`/styrelsen/dashboard/pages/${cat}/${sl}`);
    setShowNewPageForm(false);
  };

  const handleSave = async () => {
    if (!selectedPage) return;

    try {
      setIsSaving(true);
      await ProtectedPagesAPI.update(selectedPage.category, selectedPage.slug, {
        title,
        content,
        icon,
        description
      });

      setOriginalTitle(title);
      setOriginalContent(content);
      setOriginalIcon(icon);
      setOriginalDescription(description);
      
      // Update the pages list to reflect changes
      setPages(prev => prev.map(p => 
        p.category === selectedPage.category && p.slug === selectedPage.slug
          ? { ...p, title, icon, description }
          : p
      ));
      
      showToast('success', 'Sidan sparad!');
    } catch (err) {
      showToast('error', err.message || 'Kunde inte spara sidan');
      console.error('Failed to save page:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPage || !isAdmin) {
      showToast('error', 'Endast administratörer kan ta bort sidor');
      return;
    }

    const confirmed = window.confirm(`Är du säker på att du vill ta bort sidan "${selectedPage.title}"? Detta kan inte ångras.`);
    if (!confirmed) return;

    try {
      setIsDeleting(true);
      await ProtectedPagesAPI.delete(selectedPage.category, selectedPage.slug);
      
      // Remove from local state
      setPages(prev => prev.filter(p => !(p.category === selectedPage.category && p.slug === selectedPage.slug)));
      
      // Clear selection
      setSelectedPage(null);
      setTitle('');
      setContent('');
      setOriginalTitle('');
      setOriginalContent('');
      
      navigate('/styrelsen/dashboard/pages');
      showToast('success', 'Sidan borttagen!');
    } catch (err) {
      showToast('error', err.message || 'Kunde inte ta bort sidan');
      console.error('Failed to delete page:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateNewPage = async () => {
    if (!isAdmin) {
      showToast('error', 'Endast administratörer kan skapa nya sidor');
      return;
    }

    if (!newPageCategory || !newPageSlug || !newPageTitle) {
      showToast('error', 'Kategori, URL-slug och titel är obligatoriska');
      return;
    }

    // Validate slug format
    const slugRegex = /^[a-z0-9-]+$/;
    if (!slugRegex.test(newPageSlug)) {
      showToast('error', 'URL-slug får endast innehålla små bokstäver, siffror och bindestreck');
      return;
    }

    try {
      setIsSaving(true);
      await ProtectedPagesAPI.create({
        category: newPageCategory,
        slug: newPageSlug,
        title: newPageTitle,
        content: newPageContent || '# ' + newPageTitle + '\n\nInnehåll kommer snart...',
        icon: newPageIcon,
        description: newPageDescription
      });

      // Refresh pages list
      const data = await PagesAPI.getAll();
      setPages(data.pages || []);

      // Navigate to new page
      navigate(`/styrelsen/dashboard/pages/${newPageCategory}/${newPageSlug}`);
      setShowNewPageForm(false);
      
      // Reset new page form
      setNewPageCategory('foreningen');
      setNewPageSlug('');
      setNewPageTitle('');
      setNewPageContent('');
      setNewPageIcon('');
      setNewPageDescription('');
      
      showToast('success', 'Ny sida skapad!');
    } catch (err) {
      if (err.message?.includes('already exists') || err.message?.includes('Conflict')) {
        showToast('error', 'En sida med denna URL finns redan');
      } else {
        showToast('error', err.message || 'Kunde inte skapa sidan');
      }
      console.error('Failed to create page:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (!isDirty) return;

    const confirmed = window.confirm('Återställa alla ändringar?');
    if (confirmed) {
      setTitle(originalTitle);
      setContent(originalContent);
      setIcon(originalIcon);
      setDescription(originalDescription);
    }
  };

  // Auto-resize textarea
  const handleTextareaChange = (e) => {
    setContent(e.target.value);
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(300, textarea.scrollHeight)}px`;
    }
  };

  // Group pages by category
  const pagesByCategory = pages.reduce((acc, page) => {
    if (!acc[page.category]) {
      acc[page.category] = [];
    }
    acc[page.category].push(page);
    return acc;
  }, {});

  // Sort categories
  const categoryOrder = ['foreningen', 'ansvarsomraden', 'nedingen'];
  const sortedCategories = categoryOrder.filter(cat => pagesByCategory[cat]);

  // Mobile view - show message
  if (isMobile) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 56px)',
        padding: '2rem',
        textAlign: 'center',
        fontFamily: "'Lato', sans-serif"
      }}>
        <div>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💻</div>
          <h2 style={{ fontFamily: '"Playfair Display", serif', color: 'var(--forest)', marginBottom: '0.5rem' }}>
            Sidinnehåll
          </h2>
          <p style={{ color: 'var(--text-soft)', marginBottom: '1rem' }}>
            Sidinnehåll är endast tillgängligt via desktop.
          </p>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-mid)' }}>
            Vänligen använd en dator för att redigera sidor.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      fontFamily: "'Lato', sans-serif"
    }}>
      {/* Toast Notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          borderRadius: '4px',
          color: '#fff',
          fontWeight: 500,
          zIndex: 1000,
          background: toast.type === 'success' ? '#5C7A3E' : '#B03030',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          animation: 'slideIn 0.3s ease'
        }}>
          {toast.message}
        </div>
      )}

      {/* Sidebar */}
      <aside style={{
        width: '260px',
        minWidth: '260px',
        background: '#F5F0E8',
        borderRight: '1px solid #D8D0C0',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Sidebar Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #D8D0C0',
          background: '#2C4A2E',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: '1.1rem',
            color: '#fff',
            margin: 0
          }}>
            Sidor
          </h2>
          {isAdmin && (
            <button
              onClick={() => setShowNewPageForm(true)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: '#fff',
                width: '28px',
                height: '28px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1.2rem',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Lägg till ny sida"
            >
              +
            </button>
          )}
        </div>

        {/* Pages List */}
        <div style={{ padding: '1rem 0', flex: 1 }}>
          {loadingPages ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#7A8070' }}>
              Laddar...
            </div>
          ) : (
            sortedCategories.map(cat => (
              <div key={cat} style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  padding: '0.5rem 1.25rem',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#7A8070'
                }}>
                  {categoryNames[cat]}
                </div>
                {pagesByCategory[cat]
                  .sort((a, b) => a.title.localeCompare(b.title, 'sv'))
                  .map(page => {
                    const isSelected = selectedPage?.category === page.category && selectedPage?.slug === page.slug;
                    return (
                      <button
                        key={`${page.category}-${page.slug}`}
                        onClick={() => handlePageSelect(page.category, page.slug)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '0.6rem 1.25rem',
                          textAlign: 'left',
                          background: isSelected ? '#E8E0D0' : 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          color: isSelected ? '#1E2820' : '#4A5240',
                          fontWeight: isSelected ? 500 : 400
                        }}
                      >
                        {page.title}
                      </button>
                    );
                  })}
              </div>
            ))
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: '#FDFCF9'
      }}>
        {/* New Page Form */}
        {showNewPageForm ? (
          <div style={{
            flex: 1,
            padding: '2rem',
            overflowY: 'auto'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '2rem'
            }}>
              <h2 style={{
                fontFamily: '"Playfair Display", serif',
                fontSize: '1.5rem',
                color: '#2C4A2E',
                margin: 0
              }}>
                Lägg till ny sida
              </h2>
              <button
                onClick={() => setShowNewPageForm(false)}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #D8D0C0',
                  borderRadius: '4px',
                  background: '#fff',
                  cursor: 'pointer'
                }}
              >
                Avbryt
              </button>
            </div>

            <div style={{ maxWidth: '600px' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  marginBottom: '0.5rem'
                }}>
                  Sektion *
                </label>
                <select
                  value={newPageCategory}
                  onChange={(e) => setNewPageCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: "'Lato', sans-serif",
                    background: '#fff'
                  }}
                >
                  {categoryOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  marginBottom: '0.5rem'
                }}>
                  URL-slug * (t.ex. "nya-sidan", endast små bokstäver och bindestreck)
                </label>
                <input
                  type="text"
                  value={newPageSlug}
                  onChange={(e) => setNewPageSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  placeholder="nya-sidan"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: "'Lato', sans-serif"
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  marginBottom: '0.5rem'
                }}>
                  Titel *
                </label>
                <input
                  type="text"
                  value={newPageTitle}
                  onChange={(e) => setNewPageTitle(e.target.value)}
                  placeholder="Sidans titel"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: "'Lato', sans-serif"
                  }}
                />
              </div>

              <NewPageIconSelector />

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  marginBottom: '0.5rem'
                }}>
                  Kort beskrivning (valfritt, visas på startsidan)
                </label>
                <input
                  type="text"
                  value={newPageDescription}
                  onChange={(e) => setNewPageDescription(e.target.value)}
                  placeholder="En kort beskrivning..."
                  maxLength={100}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: "'Lato', sans-serif"
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  marginBottom: '0.5rem'
                }}>
                  Innehåll (Markdown, valfritt)
                </label>
                <MarkdownToolbar
                  textareaRef={newPageTextareaRef}
                  content={newPageContent}
                  setContent={setNewPageContent}
                  disabled={false}
                />
                <textarea
                  ref={newPageTextareaRef}
                  value={newPageContent}
                  onChange={(e) => setNewPageContent(e.target.value)}
                  placeholder="# Rubrik\n\nInnehåll..."
                  rows={10}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    fontFamily: "'Monaco', 'Menlo', 'Consolas', monospace",
                    fontSize: '14px',
                    lineHeight: 1.6,
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={handleCreateNewPage}
                  disabled={isSaving}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '4px',
                    background: '#2C4A2E',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    opacity: isSaving ? 0.7 : 1
                  }}
                >
                  {isSaving ? 'Skapar...' : 'Skapa sida'}
                </button>
                <button
                  onClick={() => setShowNewPageForm(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    background: '#fff',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        ) : selectedPage ? (
          <>
            {/* Header */}
            <header style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.5rem',
              borderBottom: '1px solid #D8D0C0',
              background: '#fff',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <h1 style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '1.1rem',
                  color: '#2C4A2E',
                  margin: 0,
                  fontWeight: 400
                }}>
                  Redigera sida
                </h1>
                <span style={{
                  fontSize: '0.8rem',
                  color: '#7A8070',
                  fontWeight: 300
                }}>
                  /{selectedPage.category}/{selectedPage.slug}
                </span>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={handleReset}
                  disabled={!isDirty || isSaving}
                  style={{
                    padding: '0.6rem 1.2rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    background: '#fff',
                    color: !isDirty ? '#7A8070' : '#4A5240',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    cursor: !isDirty ? 'not-allowed' : 'pointer',
                    opacity: !isDirty ? 0.6 : 1
                  }}
                >
                  Återställ
                </button>
                {isAdmin && (
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    style={{
                      padding: '0.6rem 1.2rem',
                      border: '1px solid #B03030',
                      borderRadius: '4px',
                      background: '#fff',
                      color: '#B03030',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      cursor: isDeleting ? 'not-allowed' : 'pointer',
                      opacity: isDeleting ? 0.7 : 1
                    }}
                  >
                    {isDeleting ? 'Tar bort...' : 'Ta bort'}
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={!isDirty || isSaving || !canEdit}
                  title={canEdit ? '' : 'Kräver redaktörs- eller administratörsbehörighet'}
                  style={{
                    padding: '0.6rem 1.4rem',
                    border: 'none',
                    borderRadius: '4px',
                    background: isDirty && !isSaving && canEdit ? '#2C4A2E' : '#9CA3AF',
                    color: '#fff',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    cursor: !isDirty || isSaving || !canEdit ? 'not-allowed' : 'pointer',
                    opacity: !isDirty || isSaving || !canEdit ? 0.7 : 1
                  }}
                >
                  {isSaving ? 'Sparar...' : 'Spara'}
                </button>
              </div>
            </header>

            {/* Split Editor/Preview */}
            <div style={{
              display: 'flex',
              flex: 1,
              overflow: 'hidden'
            }}>
              {/* Editor Pane */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                padding: '1.5rem',
                borderRight: '1px solid #D8D0C0'
              }}>
                {loadingPage ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#7A8070' }}>
                    Laddar...
                  </div>
                ) : (
                  <>
                    {/* Title Input */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: '#7A8070',
                        marginBottom: '0.5rem'
                      }}>
                        Titel
                      </label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        disabled={!canEdit}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          border: '1px solid #D8D0C0',
                          borderRadius: '4px',
                          fontSize: '1rem',
                          fontFamily: "'Lato', sans-serif",
                          background: canEdit ? '#fff' : '#F5F5F5',
                          color: '#1E2820',
                          cursor: canEdit ? 'text' : 'not-allowed'
                        }}
                      />
                    </div>

                    {/* Icon Input */}
                    <IconSelector value={icon} onChange={canEdit ? setIcon : () => {}} disabled={!canEdit} />

                    {/* Description Input */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: '#7A8070',
                        marginBottom: '0.5rem'
                      }}>
                        Kort beskrivning (visas på startsidan)
                      </label>
                      <input
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="En kort beskrivning av sidan..."
                        maxLength={100}
                        disabled={!canEdit}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          border: '1px solid #D8D0C0',
                          borderRadius: '4px',
                          fontSize: '1rem',
                          fontFamily: "'Lato', sans-serif",
                          background: canEdit ? '#fff' : '#F5F5F5',
                          color: '#1E2820',
                          cursor: canEdit ? 'text' : 'not-allowed'
                        }}
                      />
                      <div style={{ fontSize: '0.75rem', color: '#7A8070', marginTop: '0.25rem', textAlign: 'right' }}>
                        {description.length}/100 tecken
                      </div>
                    </div>

                    {/* Content Textarea */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: '#7A8070',
                        marginBottom: '0.5rem'
                      }}>
                        Innehåll (Markdown)
                      </label>
                      <MarkdownToolbar
                        textareaRef={textareaRef}
                        content={content}
                        setContent={setContent}
                        disabled={!canEdit}
                      />
                      <textarea
                        ref={textareaRef}
                        value={content}
                        onChange={canEdit ? handleTextareaChange : () => {}}
                        disabled={!canEdit}
                        style={{
                          width: '100%',
                          minHeight: '300px',
                          padding: '1rem',
                          border: '1px solid #D8D0C0',
                          borderRadius: '4px',
                          fontFamily: "'Monaco', 'Menlo', 'Consolas', monospace",
                          fontSize: '14px',
                          lineHeight: 1.6,
                          resize: canEdit ? 'vertical' : 'none',
                          background: canEdit ? '#fff' : '#F5F5F5',
                          color: '#1E2820',
                          flex: 1,
                          cursor: canEdit ? 'text' : 'not-allowed'
                        }}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Preview Pane */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '1.5rem',
                background: '#F5F0E8'
              }}>
                <div style={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  marginBottom: '0.75rem'
                }}>
                  Förhandsgranskning
                </div>
                <div style={{
                  background: '#fff',
                  border: '1px solid #D8D0C0',
                  borderRadius: '6px',
                  padding: '2rem',
                  minHeight: '200px'
                }}>
                  <h1 style={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: '1.8rem',
                    color: '#2C4A2E',
                    marginBottom: '1.5rem',
                    fontWeight: 400
                  }}>
                    {title}
                  </h1>
                  <Markdown content={content} />
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Placeholder when no page selected */
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            color: '#7A8070'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }}>
              📝
            </div>
            <p style={{ fontSize: '1.1rem', fontStyle: 'italic' }}>
              Välj en sida från menyn till vänster
            </p>
            <p style={{ fontSize: '0.9rem', marginTop: '0.5rem', opacity: 0.7 }}>
              för att börja redigera
            </p>
            {isAdmin && (
              <button
                onClick={() => setShowNewPageForm(true)}
                style={{
                  marginTop: '2rem',
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '4px',
                  background: '#2C4A2E',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                + Lägg till ny sida
              </button>
            )}
          </div>
        )}
      </main>

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .editor-container {
            flex-direction: column !important;
          }
          aside {
            width: 100% !important;
            min-width: auto !important;
            max-height: 200px;
            border-right: none !important;
            border-bottom: 1px solid #D8D0C0;
          }
          main > div {
            flex-direction: column !important;
          }
          main > div > div:first-child {
            border-right: none !important;
            border-bottom: 1px solid #D8D0C0;
          }
        }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
